import React, { ReactNode, useState } from 'react';
import { Text, Flex, Box, Tooltip } from '@chakra-ui/react';
import { ButtonIcon } from '@morressier/alembic';
import { parseStringWithLinks } from 'src/utils/parseStringWithLinks';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons';

export const Field: React.FC<{
  label: string;
  children: ReactNode;
  isRequired?: boolean;
}> = ({ label, children, isRequired = false }) => (
  <Flex flexDirection="column" alignItems="flex-start" gap={2} width="100%">
    <Flex alignItems="flex-start" width="100%" gap={2}>
      <Text textStyle="headingItem">
        {label}
        {isRequired && (
          <Box
            as="span"
            color="actionSecondaryCautionContent"
            textStyle="headingItem"
          >
            *
          </Box>
        )}
      </Text>
    </Flex>
    {children}
  </Flex>
);

export const FieldLabel: React.FC<{
  label: string;
  subText: ReactNode;
  isRequired?: boolean;
  textStyle?: string;
  subTextProps?: Record<string, any>;
  isInfoTextAvailable?: boolean;
  helpText?: ReactNode;
}> = ({
  subText,
  label,
  isRequired = true,
  textStyle = 'headingItem',
  subTextProps = {},
  helpText = ''
}) => {
  /**
   * The tooltip is controlled so it stays shut when a surrounding modal opens
   * and focus lands on the icon: an uncontrolled tooltip opens on focus, which
   * flashes the first help text at whoever just opened the dialog. The icon is
   * kept out of the tab order for the same reason, matching `Figures`.
   */
  const [isHelpTooltipOpen, setIsHelpTooltipOpen] = useState(false);

  return (
    <Box id={label}>
      <Flex alignItems="flex-start">
        <Text textStyle={textStyle}>
          {parseStringWithLinks(label)}

          {isRequired && label.trim() && (
            <Box
              as="span"
              color="actionSecondaryCautionContent"
              textStyle={textStyle}
            >
              *
            </Box>
          )}
        </Text>

        {helpText && label.trim() && (
          <Tooltip
            hasArrow
            shouldWrapChildren
            placement="top"
            isOpen={isHelpTooltipOpen}
            label={
              <Text textStyle="textContext">
                {typeof helpText === 'string'
                  ? parseStringWithLinks(helpText)
                  : helpText}
              </Text>
            }
            {...((label.trim() === 'Conflict of Interest' ||
              label.trim() === 'If so, please state:' ||
              label.trim() ===
                'Does submission have Conflict of Interest?') && {
              maxW: 'min(700px, 90vw)',
              p: 3
            })}
            borderRadius={4}
            data-cy="info-tooltip"
          >
            <span
              onMouseEnter={() => setIsHelpTooltipOpen(true)}
              onMouseLeave={() => setIsHelpTooltipOpen(false)}
              onTouchStart={() => setIsHelpTooltipOpen(prev => !prev)}
            >
              <ButtonIcon
                size="small"
                ariaLabel={`More information about ${label}`}
                icon={<FontAwesomeIcon icon={faInfoCircle} />}
                data-cy="info-icon"
                tabIndex={-1}
              />
            </span>
          </Tooltip>
        )}
      </Flex>
      {subText && (
        <Text
          textStyle="textContext"
          whiteSpace={
            typeof subText === 'string' && subText.trim() === ''
              ? 'normal'
              : 'pre-wrap'
          }
          {...subTextProps}
        >
          {typeof subText === 'string'
            ? parseStringWithLinks(subText)
            : subText}
        </Text>
      )}
    </Box>
  );
};
